import { Download, FileText, Printer, X } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { fmtN } from './figma-ui';
import { requestSystemInvoicePrint } from '../../printing/invoiceSystemPrint';

export type InvoiceLine = {
  name: string;
  sku: string;
  qty: number;
  unitPrice: number;
  tax: number;
  discount: number;
};

export type InvoiceSale = {
  id: string;
  date: string;
  time: string;
  cashier: string;
  branch: string;
  paymentMethod: string;
  items: InvoiceLine[];
};

export function InvoiceModal({ sale, onClose }: { sale: InvoiceSale; onClose: () => void }) {
  const subtotalHT = sale.items.reduce((s, i) => s + i.unitPrice * i.qty * (1 - i.discount / 100), 0);
  const totalDiscount = sale.items.reduce((s, i) => s + i.unitPrice * i.qty * (i.discount / 100), 0);
  const totalTax = sale.items.reduce(
    (s, i) => s + i.unitPrice * i.qty * (1 - i.discount / 100) * (i.tax / 100),
    0,
  );
  const totalTTC = subtotalHT + totalTax;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm print:static print:bg-white print:p-0" onClick={onClose}>
      <style>{`@media print { body * { visibility: hidden !important; } .invoice-print-root, .invoice-print-root * { visibility: visible !important; } .invoice-print-root { position: absolute; left: 0; top: 0; width: 100%; } }`}</style>
      <div className="invoice-print-root bg-white rounded-2xl shadow-2xl w-[680px] max-h-[90vh] overflow-y-auto print:max-h-none print:shadow-none print:rounded-none print:w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-brand" />
            <span className="font-bold text-gray-800">Facture — {sale.id}</span>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button
              type="button"
              onClick={() => requestSystemInvoicePrint()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white rounded-lg text-sm font-medium"
            >
              <Printer size={13} />
              Imprimer
            </button>
            <button
              type="button"
              onClick={() => requestSystemInvoicePrint()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
            >
              <Download size={13} />
              PDF
            </button>
            <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <X size={15} />
            </button>
          </div>
        </div>
        <div className="p-8 space-y-6 invoice-print-body">
          <div className="flex items-start justify-between">
            <div>
              <BrandLogo size="md" />
              <div className="mt-3 text-sm text-gray-500 space-y-0.5">
                <div>{sale.branch}</div>
              </div>
            </div>
            <div className="text-right text-sm text-gray-600 space-y-1">
              <div className="text-2xl font-bold text-brand uppercase tracking-widest">Facture</div>
              <div>N° : <span className="font-semibold font-mono">{sale.id}</span></div>
              <div>Date : {sale.date}</div>
              <div>Heure : {sale.time}</div>
              <div>Caissier : {sale.cashier}</div>
            </div>
          </div>
          <div className="border-t-2 border-brand" />
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['#', 'Désignation', 'SKU', 'QTE', 'Prix U. HT', 'Rem.%', 'TVA', 'Total TTC'].map((h) => (
                  <th key={h} className="py-2.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide px-2 text-left last:text-right">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sale.items.map((item, i) => {
                const lineHT = item.unitPrice * item.qty * (1 - item.discount / 100);
                const lineTTC = lineHT * (1 + item.tax / 100);
                return (
                  <tr key={i}>
                    <td className="py-2 px-2 text-gray-400">{i + 1}</td>
                    <td className="py-2 px-2 font-medium">{item.name}</td>
                    <td className="py-2 px-2 font-mono text-xs text-gray-400">{item.sku}</td>
                    <td className="py-2 px-2">{item.qty}</td>
                    <td className="py-2 px-2 tabular-nums">{fmtN(item.unitPrice)}</td>
                    <td className="py-2 px-2 text-amber-600">{item.discount > 0 ? `-${item.discount}%` : '—'}</td>
                    <td className="py-2 px-2">{item.tax}%</td>
                    <td className="py-2 px-2 tabular-nums font-bold text-right">{fmtN(lineTTC)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex justify-end">
            <div className="w-72 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-500"><span>Total HT</span><span className="tabular-nums">{fmtN(subtotalHT)} TND</span></div>
              {totalDiscount > 0 && (
                <div className="flex justify-between text-amber-600"><span>Total Remise</span><span className="tabular-nums">-{fmtN(totalDiscount)} TND</span></div>
              )}
              <div className="flex justify-between text-gray-500"><span>Total TVA</span><span className="tabular-nums">{fmtN(totalTax)} TND</span></div>
              <div className="border-t-2 border-brand pt-2 flex justify-between font-bold text-lg">
                <span>Total TTC</span>
                <span className="tabular-nums text-brand">{fmtN(totalTTC)} TND</span>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4 flex justify-between text-sm">
            <div><div className="font-medium text-gray-700">Mode de règlement</div><div className="text-gray-500">{sale.paymentMethod}</div></div>
            <div className="text-xs text-gray-400 italic">Merci de votre confiance — InCashFlow</div>
          </div>
        </div>
      </div>
    </div>
  );
}
